import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormikContext } from "formik";

import type { BehaviorFunction } from "api/types/aba";
import type { GoalFormValues } from "./GoalBuilderPanel.types";
import { HYPOTHESIZED_FUNCTION_OPTIONS } from "../../shared/behaviorFunctions";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface FieldGroupProps {
  label: string;
  description?: string;
  required?: boolean;
  children: React.ReactNode;
}

const FieldGroup: React.FC<FieldGroupProps> = ({ label, description, required, children }) => (
  <Stack gap={2}>
    <Box>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      {required && (
        <Typography component="span" variant="body2" color="error.main">
          {" "}
          *
        </Typography>
      )}
      {description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Stack>
);

// ---------------------------------------------------------------------------
// GoalFormSection15
// ---------------------------------------------------------------------------

/**
 * Section 1.5 — Behavior reduction.
 * Rendered conditionally between Section 1 and Section 2 when
 * goal_type === "behavior-reduction".
 *
 * Fields: operational_definition, hypothesized_functions (checkboxes),
 * common_antecedents.
 */
const GoalFormSection15: React.FC = () => {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } =
    useFormikContext<GoalFormValues>();

  const handleFunctionToggle = (fn: BehaviorFunction) => {
    const current = values.hypothesized_functions;
    const updated = current.includes(fn) ? current.filter((f) => f !== fn) : [...current, fn];
    setFieldValue("hypothesized_functions", updated);
    setFieldTouched("hypothesized_functions", true, false);
  };

  const functionsError =
    touched.hypothesized_functions && typeof errors.hypothesized_functions === "string"
      ? errors.hypothesized_functions
      : null;

  return (
    <Box
      sx={{
        bgcolor: (theme) => (theme.palette as any).grayscale?.[50] ?? "#F6F7F8",
        borderRadius: 3,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Section header */}
      <Box pt={2} sx={{ px: 1 }}>
        <Typography component="h3" variant="subtitle2">
          Section 1.5 — Behavior reduction
        </Typography>
        <Typography component="p" variant="caption" color="text.secondary">
          Function, replacement behaviors, and common antecedents
        </Typography>
      </Box>

      {/* Fields card */}
      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: (theme) => (theme.palette as any).grayscale?.[100] ?? "#EAECEF",
          borderRadius: 3,
          p: 6,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <FieldGroup
          label="Operational Definition"
          description="A precise, observable, and measurable description of the target behavior"
          required
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="operational_definition"
            placeholder="e.g., Hitting is defined as any instance of using a closed or open hand to make forceful contact with another person's body"
            value={values.operational_definition}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.operational_definition && Boolean(errors.operational_definition)}
            helperText={touched.operational_definition && errors.operational_definition}
            aria-label="Operational definition"
          />
        </FieldGroup>

        <FieldGroup
          label="Hypothesized Function(s)"
          description="Select all functions that may maintain this behavior"
          required
        >
          <FormGroup>
            {HYPOTHESIZED_FUNCTION_OPTIONS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                control={
                  <Checkbox
                    checked={values.hypothesized_functions.includes(opt.value)}
                    onChange={() => handleFunctionToggle(opt.value)}
                    color="primary"
                    size="small"
                    aria-label={opt.label}
                  />
                }
                label={<Typography variant="body2">{opt.label}</Typography>}
                sx={{ mb: 1 }}
              />
            ))}
          </FormGroup>
          {functionsError && (
            <Typography variant="caption" color="error.main">
              {functionsError}
            </Typography>
          )}
        </FieldGroup>

        <FieldGroup
          label="Common Antecedents"
          description="Situations or conditions that commonly precede the behavior"
          required
        >
          <TextField
            multiline
            minRows={4}
            fullWidth
            name="common_antecedents"
            value={values.common_antecedents}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.common_antecedents && Boolean(errors.common_antecedents)}
            helperText={touched.common_antecedents && errors.common_antecedents}
            aria-label="Common antecedents"
          />
        </FieldGroup>
      </Box>
    </Box>
  );
};

export default GoalFormSection15;
